import { isDashboardAccessBlockedError } from './dashboard-access-errors'
import { isIgnorableGroupCodeRedeemError } from './group-code-errors'

export type AuthErrorKind =
  | 'user-cancelled'
  | 'browser-interrupted'
  | 'missing-credentials'
  | 'missing-refresh-token'
  | 'other'

export class MissingAccessTokenError extends Error {
  constructor(message = 'No access token') {
    super(message)
    this.name = 'MissingAccessTokenError'
  }
}

function getAuthErrorCode(error: {
  code?: unknown
  name?: unknown
}): string | null {
  if (typeof error.code === 'string') return error.code
  return typeof error.name === 'string' ? error.name : null
}

function getAuthErrorMessage(error: { message?: unknown }): string | null {
  return typeof error.message === 'string' ? error.message : null
}

export function getAuthErrorKind(error: unknown): AuthErrorKind | null {
  if (error == null || typeof error !== 'object') return null

  const authError = error as { code?: unknown; message?: unknown }

  const code = getAuthErrorCode(authError)
  const message = getAuthErrorMessage(authError)

  if (
    code === 'USER_CANCELLED' ||
    code === 'USER_CANCELED' ||
    code === 'a0.session.user_cancelled' ||
    message?.includes('The user cancelled the Web Auth operation.') === true ||
    message?.includes('The user canceled the Web Auth operation.') === true ||
    message?.includes('User cancelled the Auth') === true
  ) {
    return 'user-cancelled'
  }

  if (
    code === 'a0.session.browser_terminated' ||
    message?.includes(
      'The browser window was closed by a new instance of the application',
    ) === true
  ) {
    return 'browser-interrupted'
  }

  if (
    code === 'NO_REFRESH_TOKEN' ||
    code === 'missing_refresh_token' ||
    message?.includes('does not contain a refresh token') === true ||
    message?.includes('Missing Refresh Token') === true
  ) {
    return 'missing-refresh-token'
  }

  if (
    code === 'NO_CREDENTIALS' ||
    message?.includes('No credentials were found in the store.') === true
  ) {
    return 'missing-credentials'
  }

  return 'other'
}

export function isMissingCredentialsError(error: unknown): boolean {
  return getAuthErrorKind(error) === 'missing-credentials'
}

export function isMissingAccessTokenError(
  error: unknown,
): error is MissingAccessTokenError {
  return error instanceof MissingAccessTokenError
}

export function isInvalidSessionAuthError(
  error: unknown,
): error is { code: string; message?: string } {
  return getAuthErrorKind(error) === 'missing-refresh-token'
}

export function isIgnorableAuthSessionError(error: unknown): boolean {
  const kind = getAuthErrorKind(error)

  return (
    kind === 'user-cancelled' ||
    kind === 'browser-interrupted' ||
    kind === 'missing-credentials' ||
    kind === 'missing-refresh-token'
  )
}

export function shouldCaptureSentryError(error: unknown): boolean {
  if (isIgnorableGroupCodeRedeemError(error)) {
    return false
  }

  if (isMissingAccessTokenError(error)) {
    return false
  }

  if (isDashboardAccessBlockedError(error)) {
    return false
  }

  return !isIgnorableAuthSessionError(error)
}

export function normalizeAuthError(
  error: Error | undefined,
): Error | undefined {
  if (error == null) return undefined
  return getAuthErrorKind(error) === 'other' ? error : undefined
}
